import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { ContractMilestone, ContractTimelineResponse } from '../admin-dashboard.models';

@Component({
  selector: 'app-contract-timeline',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzAlertModule,
    NzCardModule,
    NzEmptyModule,
    NzGridModule,
    NzListModule,
    NzSkeletonModule,
    NzTagModule,
  ],
  templateUrl: './contract-timeline.component.html',
  styleUrl: './contract-timeline.component.less',
})
export class ContractTimelineComponent {
  @Input() timeline: ContractTimelineResponse | null = null;
  @Input() loading = false;

  readonly groupOrder: ContractMilestone['groupId'][] = [
    'needs_attention',
    'active',
    'upcoming',
    'completed',
    'archived',
    'informational',
  ];

  constructor(private readonly translate: TranslateService) {}

  get isFreePlan(): boolean {
    return this.timeline?.plan === 'free';
  }

  get groupedMilestones(): Array<{ groupId: ContractMilestone['groupId']; items: ContractMilestone[] }> {
    const groups = new Map<ContractMilestone['groupId'], ContractMilestone[]>();
    for (const groupId of this.groupOrder) {
      groups.set(groupId, []);
    }
    for (const milestone of this.timeline?.milestones || []) {
      const bucket = groups.get(milestone.groupId) || [];
      bucket.push(milestone);
      groups.set(milestone.groupId, bucket);
    }
    return this.groupOrder
      .map((groupId) => ({ groupId, items: groups.get(groupId) || [] }))
      .filter((group) => group.items.length > 0);
  }

  get nextMilestoneSummary(): string {
    const nextMilestone = this.timeline?.nextImportantMilestone;
    if (!nextMilestone) {
      return this.translate.instant('ADMIN_DASHBOARD.CONTRACT_TIMELINE.NO_UPCOMING');
    }
    return this.translate.instant(nextMilestone.descriptionKey || nextMilestone.labelKey, nextMilestone.params || {});
  }

  statusColor(status: ContractMilestone['status']): string {
    switch (status) {
      case 'completed':
        return 'green';
      case 'current':
        return 'blue';
      case 'warning':
        return 'gold';
      case 'overdue':
      case 'critical':
        return 'red';
      case 'action_required':
        return 'volcano';
      case 'informational':
        return 'default';
      default:
        return 'default';
    }
  }

  groupTitle(groupId: ContractMilestone['groupId']): string {
    return this.translate.instant(`ADMIN_DASHBOARD.CONTRACT_TIMELINE.GROUPS.${groupId.toUpperCase()}`);
  }

  statusLabel(status: ContractMilestone['status']): string {
    return this.translate.instant(`ADMIN_DASHBOARD.CONTRACT_TIMELINE.STATUS.${status.toUpperCase()}`);
  }

  milestoneText(milestone: ContractMilestone): string {
    const key = milestone.descriptionKey || milestone.labelKey;
    return this.translate.instant(key, milestone.descriptionParams || {});
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const locale = (this.translate.currentLang || 'en').toLowerCase().startsWith('de') ? 'de-DE' : 'en';
    return new Intl.DateTimeFormat(locale, {
      day: locale === 'de-DE' ? '2-digit' : 'numeric',
      month: locale === 'de-DE' ? '2-digit' : 'short',
      year: 'numeric',
    }).format(date);
  }
}
